// Shared handling of the Ask CL stream's 'complete' line and of the report
// blocks / attachments stored with a message. Every stream reader in
// AppFloatingChat goes through applyStreamComplete, so none of them drops the
// charts or sources of an answer.

/** A web page the answer relied on (its Sources section is already in the text). */
export interface StreamCitation {
  url: string
  title?: string
}

/** A file sent with a message, as the history returns it. */
export interface MessageAttachment {
  name: string
  mime: string
}

/** The fields of a 'complete' line this helper reads. */
export interface StreamCompleteLine {
  fullResponse?: string
  reportBlocks?: unknown
  citations?: unknown
}

/** What the reader built from the stream before 'complete' arrived. */
export interface StreamedSoFar {
  /** Text accumulated from 'content' lines. */
  text: string
  /** A form / UI component the stream sent instead of text; it is kept as is. */
  structured?: unknown
}

interface CompletableMessage {
  id: string
  content: unknown
  reportBlocks?: any[]
  citations?: StreamCitation[]
}

/** Only the entries that are objects (a malformed entry must not break rendering). */
const objectsOnly = (value: unknown): any[] =>
  Array.isArray(value)
    ? value.filter((entry) => !!entry && typeof entry === 'object' && !Array.isArray(entry))
    : []

/**
 * Applies a 'complete' line to the bot message `messageId`: the final text (or
 * the streamed text / structured content), its report blocks and citations.
 */
export const applyStreamComplete = <T extends CompletableMessage>(
  messages: T[],
  messageId: string,
  line: StreamCompleteLine,
  streamed: StreamedSoFar
): T[] =>
  messages.map((message) =>
    message.id === messageId
      ? ({
          ...message,
          content: streamed.structured ?? (line.fullResponse || streamed.text),
          reportBlocks: objectsOnly(line.reportBlocks),
          citations: objectsOnly(line.citations).filter(
            (citation) => typeof citation.url === 'string'
          ),
        } as T)
      : message
  )

/**
 * A JSON array stored with a message (getMessagesFromBot's reportBlocks /
 * attachments). Empty when absent or unreadable. Also reads a value encoded
 * twice (an AWSJSON field holding a JSON string), as older API versions sent.
 */
export const parseStoredJsonArray = (value?: string | null): any[] => {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return objectsOnly(typeof parsed === 'string' ? JSON.parse(parsed) : parsed)
  } catch {
    return []
  }
}
